<template>
  <vee-form v-slot="{ validate, errors, setFieldError }" as="">
    <form
      ref="discussionForm"
      class="wide-single-col-form standard-form"
      method="post"
      novalidate
      @submit.prevent="validateAllAndSubmit(validate, setFieldError)"
    >
      <div class="form__collection">
        <select-input
          v-model="selectedCat"
          input-id="category_id"
          label="Topic"
          rules="required"
          :options="categoryOptions"
          @change="resetSubcats"
        />

        <select-input
          v-model="selectedSubcat"
          input-id="subcategory_id"
          label="Subtopic"
          :rules="selectedCat ? 'required' : ''"
          :options="subcategoriesOptions"
          :disabled="!selectedCat"
        />
      </div>

      <text-input
        v-model="title"
        input-id="title"
        label="Title"
        rules="required|max:115"
        maxlength="115"
      />

      <textarea-input
        v-model="body"
        input-id="body"
        label="Message"
        placeholder="What's on your mind?  Start typing here..."
        rules="required|min:20|max:100000"
        maxlength="100000"
        :large="true"
      />

      <file-input
        ref="fileInput"
        type="file"
        input-id="image"
        rules="image|size:10240"
        :accept="mimeTypes.join(',')"
        file-for="discussion"
        @change="onFileChange"
      >
        <template #label>
          <i
            v-if="!(discussionImage && !errors.image)"
            class="material-symbols-outlined"
            data-icon="imagesmode"
            aria-hidden="true"
          />
          <span v-if="!(discussionImage && !errors.image)" class="image">
            <strong>Attach an Image</strong> (10mb limit)
          </span>
        </template>
        <template #remove>
          <div v-if="discussionImage && !errors.image" class="file-remove">
            <button class="btn -image-remove" title="Remove Image" @click.prevent="removeFile">
              <span class="iconfont-menu-close" />Remove Image
            </button>
            <img :src="discussionImage" alt="" />
          </div>
        </template>
      </file-input>
      <input v-model="removeImage" name="image_remove" type="hidden" />

      <div class="posting-as">
        <h5 class="heading">Posting As</h5>
        <div class="posting-identity">
          <div class="posting-avatar">
            <img v-if="user.avatar" :alt="user.username" :src="user.avatar" />
            <div v-else class="avatar-placeholder demi">
              <span>{{ user.displayName.charAt(0) }}</span>
            </div>
          </div>
          <div class="posting-name">
            {{ user.displayName }}
          </div>
          <a href="/my/account" class="change-button">Change</a>
        </div>
      </div>

      <div v-if="isEdit" class="discussion-edit-actions">
        <div class="discussion-edit-buttons">
          <submit-button
            :submitting="submitting"
            label="Save Changes"
            submitting-label="Saving…"
            classes="-main no-margin editing"
            @submit-action="validateAllAndSubmit(validate, setFieldError)"
          />
          <a class="btn -link no-margin" :href="url">Cancel</a>
        </div>
        <a class="link small demi flex-right" href="/page/discussion-policy" target="_blank"
          >Discussion Policy</a
        >
      </div>

      <div v-else class="discussion-action">
        <div class="flex-right">
          <a class="link small demi" href="/page/discussion-policy" target="_blank"
            >Discussion Policy</a
          >
        </div>
        <submit-button
          :submitting="submitting"
          label="Publish"
          submitting-label="Publishing…"
          classes="-main -large-medium-only"
          @submit-action="validateAllAndSubmit(validate, setFieldError)"
        />
      </div>
    </form>
  </vee-form>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { initExtraValidationRules, errorBagToInitialErrors } from '@/utils/forms/validation-rules'
import { Form as VeeForm } from 'vee-validate'
import SelectInput from '@/components/forms/select-input.vue'
import TextInput from '@/components/forms/text-input.vue'
import TextareaInput from '@/components/forms/textarea-input.vue'
import FileInput from '@/components/forms/file-input.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import { fbApi, FbApiError } from '@/utils/http-api'

export default defineComponent({
  props: {
    categories: {
      type: Array,
      required: true,
    },
    initDiscussion: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    VeeForm,
    SelectInput,
    TextInput,
    TextareaInput,
    FileInput,
    SubmitButton,
  },
  data() {
    return {
      // the actual image file as a string, after the file is selected
      image: '',
      /* eslint-disable camelcase */
      selectedCat: this.initDiscussion.category_id || '',
      selectedSubcat: this.initDiscussion.subcategory_id || '',
      id: this.initDiscussion.id || 0,
      title: this.initDiscussion.title || '',
      body: this.initDiscussion.body || '',
      seoUrl: this.initDiscussion.seo_url || '',
      // on edit the url to existing file
      imageUrl: this.initDiscussion.image_url || '',
      /* eslint-enable camelcase */
      submitting: false,
      removeImage: 0,
      mimeTypes: ['image/png', 'image/jpeg', 'image/gif'],
      form: document.getElementById('discussion_form'),
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['user']),

    categoryOptions() {
      return [{ value: '', label: 'Select One' }].concat(
        this.categories.map((cat) => {
          return {
            value: cat.id,
            label: cat.title,
          }
        }),
      )
    },

    subcategoriesOptions() {
      const category = this.categories.filter((cat) => {
        return cat.id === this.selectedCat
      })

      if (!category[0]) {
        return [{ value: '', label: 'Select One' }]
      }

      return [{ value: '', label: 'Select One' }].concat(
        category[0].subcategories.map((subcat) => {
          return {
            value: subcat.id,
            label: subcat.title,
          }
        }),
      )
    },
    isEdit() {
      return Boolean(this.id)
    },
    discussionImage() {
      return this.image || this.imageUrl
    },
    url() {
      return '/community/discussion/' + this.id + '/' + this.seoUrl
    },
  },
  created() {
    initExtraValidationRules()
  },
  methods: {
    ...mapActions(useModalsStore, ['showErrorModal', 'showWasLoggedInModal']),

    onFileChange(e) {
      const target = e.target || e.dataTransfer
      const files = target && target.files
      if (!files.length) {
        return
      }

      this.createImage(files[0])
    },

    createImage(file) {
      // @TODO fix this type
      const reader = new window.FileReader()

      reader.onload = (e) => {
        this.image = e.target.result
      }

      reader.readAsDataURL(file)
    },

    removeFile() {
      this.$refs.fileInput.value = ''
      this.image = ''

      // set a flag to tell server to remove the file.  Only do this if there is an existing file
      if (this.imageUrl) {
        this.removeImage = 1
      }
      this.imageUrl = ''
    },

    resetSubcats() {
      this.selectedSubcat = ''
    },

    async validateAllAndSubmit(validate, setFieldError) {
      if (this.submitting) {
        return
      }

      this.submitting = true
      const results = await validate()

      // invalid, stop
      if (!results.valid) {
        this.submitting = false
        return
      }

      const formData = new window.FormData(this.$refs.discussionForm)
      const url = this.isEdit ? '/community/edit/' + this.id : '/community/create'

      try {
        const res = await fbApi.post(url, {
          prefix: 'web',
          skipErrorModal: true,
          body: formData,
        })

        // response is url to redirect to
        if (res.body && res.body.redirectTo) {
          window.location = res.body.redirectTo
        }
      } catch (err) {
        console.error(err)
        this.submitting = false

        if (err instanceof FbApiError) {
          if (err.status === 400 && err.body && err.body.message === 'Invalid Data') {
            // validation errors
            const errorBag = errorBagToInitialErrors(err.body.data)

            if (errorBag) {
              Object.keys(errorBag).forEach((field) => {
                setFieldError(field, errorBag[field])
              })
            }
            return
          } else if (err.status === 401 || err.status === 419) {
            // logged out / long tab
            this.showWasLoggedInModal()
            return
          }
        }

        // show error modal if not validation
        this.showErrorModal()
      }
    },
  },
})
</script>
